import { SearchTimeRange, SearchSort } from "@/main/react/page/search/types";
import { isBrowserOwnedClick, type ClickModifierState } from "@/main/navigation/NavigationPolicy";

const TIME_RANGE_DAYS: Record<Exclude<SearchTimeRange, "all">, number> = {
    week: 7,
    month: 30,
    year: 365,
};

export function stripHtml(html?: string | null): string {
    if (!html) {
        return "";
    }

    return html
        .replace(/<[^>]+>/g, " ")
        .replace(/&nbsp;/gi, " ")
        .replace(/\s+/g, " ")
        .trim();
}

export function getItemDateValue(item: unknown): number | null {
    if (!item || typeof item !== "object") {
        return null;
    }

    const candidate = item as Record<string, unknown>;
    const values = [
        candidate.createdAt,
        candidate.updatedAt,
        candidate.modifiedAt,
        candidate.creationDate,
        candidate.lastDiscussionAddedAt,
        candidate.participatedAt,
        candidate.viewerFollowingAt,
    ];

    for (const value of values) {
        if (typeof value !== "string" && typeof value !== "number" && !(value instanceof Date)) {
            continue;
        }

        const date = new Date(value as string | number | Date);
        if (!Number.isNaN(date.getTime())) {
            return date.getTime();
        }
    }

    return null;
}

export function filterItemsByTimeRange<T>(items: T[], timeRange: SearchTimeRange): T[] {
    if (timeRange === "all") {
        return items;
    }

    const days = TIME_RANGE_DAYS[timeRange];
    const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;

    return items.filter((item) => {
        const dateValue = getItemDateValue(item);
        return dateValue == null || dateValue >= cutoff;
    });
}

export function sortSearchItems<T>(items: T[], sort: SearchSort): T[] {
    if (sort !== "newest") {
        return items;
    }

    return [...items].sort((left, right) => {
        const rightDate = getItemDateValue(right) ?? 0;
        const leftDate = getItemDateValue(left) ?? 0;
        return rightDate - leftDate;
    });
}

export function dedupeItems<T>(items: T[], getId: (item: T) => string | undefined): T[] {
    const seen = new Set<string>();
    const deduped: T[] = [];

    for (const item of items) {
        const id = getId(item);
        if (!id) {
            deduped.push(item);
            continue;
        }

        if (seen.has(id)) {
            continue;
        }

        seen.add(id);
        deduped.push(item);
    }

    return deduped;
}

export function transformSearchItems<T>(
    items: T[],
    options: {
        getId: (item: T) => string | undefined;
        sort: SearchSort;
        timeRange: SearchTimeRange;
    },
): T[] {
    const deduped = dedupeItems(items, options.getId);
    const filtered = filterItemsByTimeRange(deduped, options.timeRange);
    return sortSearchItems(filtered, options.sort);
}

export function formatCountLabel(loadedCount: number, hasMore: boolean): string | null {
    if (loadedCount <= 0 && !hasMore) {
        return null;
    }

    return hasMore ? `${loadedCount}+` : String(loadedCount);
}

/**
 * True for the one click a soft navigation may intercept: primary button, no modifier.
 *
 * <p>Shared by the tab strip and the pager so the two controls cannot drift into disagreeing about
 * what a real link means on this page. Cmd/Ctrl/Shift/Alt-click and middle-click must fall through to
 * the browser, or the anchor's `href` is decoration — which is exactly what `DISCOVERY-26` found when
 * the pager was `href="#"` while the tabs one row above were real URLs.
 */
export function isPlainLeftClick(event: ClickModifierState): boolean {
    // Shared predicate since 2026-09-07 (`FA-R2`). The exported name stays — the tab strip and the
    // pager both read it, and "plain left click" is the right way round for them — but the decision
    // is `NavigationPolicy`'s. Deliberately `!isBrowserOwnedClick` rather than
    // `isPageInterceptableClick`: neither caller consulted `defaultPrevented` and this is a hoist,
    // not a behaviour change.
    return !isBrowserOwnedClick(event);
}

export function getShorterQuery(query: string): string {
    const parts = query.trim().split(/\s+/).filter(Boolean);
    if (parts.length <= 1) {
        return query.trim();
    }

    return parts.slice(0, -1).join(" ");
}
